import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';
import axios from 'axios';
import moment from 'moment';


interface IRegistrationsContext {
    isLoading: boolean
    search: ISearch
    registrations: IRegistration[]
    recordCount: number
    selectedItems: string[]

    updateSearch: (newSearch: ISearch) => void
    changePage: (page: number) => void
    doSearch: () => void
    selectAllRegistrations: (all: boolean) => void
    updateSelectedItems: (registrationId: string) => void
    deleteRegistration: (registration: IRegistration) => Promise<void>
    restoreRegistration: (registration: IRegistration) => Promise<void>
}

interface IRegistration {
    email: string
    extensionMonths: number
    firstName: string
    hasWarrantyExtension: boolean
    isPreGolive: boolean
    isDeleted: boolean
    unlimited: boolean
    lastName: string
    registrationDate: moment.Moment
    registrationId: string
    sku: string
    userCountryCode: string
}

interface ISearch {
    pageNr: number
    pageSize: number
    sku: string
    firstName: string
    lastName: string
    email: string
    claimNumber: string
    isDeleted: boolean
}

export let registrationsContext = React.createContext({} as IRegistrationsContext);

let { Provider } = registrationsContext;

let RegistrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let location = useLocation();
    let history = useHistory();
    let { email } = useParams<{ email?: string }>();

    let [isLoading, setLoading] = useState(false);
    let [registrations, setRegistrations] = useState<IRegistration[]>([]);
    let [recordCount, setRecordCount] = useState(0);
    let [selectedItems, setSelectedItems] = useState<string[]>([]);
    let [search, setSearch] = useState<ISearch>({
        email: email || '',
        firstName: '',
        lastName: '',
        pageNr: 0,
        pageSize: 20,
        sku: '',
        claimNumber: "",
        isDeleted: false
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])


    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch({
                ...search,
                ...state.search
            })
        }

        searchRegistrations({
            ...search,
            ...state.search
        });
    }

    let doSearch = () => {
        let request = {
            ...search,
            pageNr: 0
        }

        setSelectedItems([]);

        history.push(location.pathname, { search: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let selectAllRegistrations = (all: boolean) => {
        let selected: string[] = [];

        if (all === true)
            registrations.forEach(reg => selected.push(reg.registrationId));

        setSelectedItems([...selected]);
    }

    let updateSelectedItems = (registrationId: string) => {
        let idx = selectedItems.indexOf(registrationId);
        if (idx === -1)
            selectedItems.push(registrationId);
        else
            selectedItems.splice(idx, 1);

        setSelectedItems([...selectedItems]);
    }

    let changePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        setSelectedItems([]);
        history.push(location.pathname, { search: request });
    }

    let deleteRegistration = async (registration: IRegistration) => {
        if (window.confirm("Are you sure that you want to delete this registration?")) {
            try {
                await axios.delete(`/api/Product/DeleteRegistration/${registration.registrationId}`);

                let idx = registrations.findIndex(x => x.registrationId === registration.registrationId);
                if (idx !== -1) {
                    registrations.splice(idx, 1, {
                        ...registration,
                        isDeleted: true
                    })
                }
            } catch (err) { }
        }
    }

    let restoreRegistration = async (registration: IRegistration) => {
        if (window.confirm("Are you sure that you want to restore this deleted registration?")) {
            try {
                await axios.put(`/api/Product/RestoreRegistration/${registration.registrationId}`);

                let idx = registrations.findIndex(x => x.registrationId === registration.registrationId);
                if (idx !== -1) {
                    registrations.splice(idx, 1, {
                        ...registration,
                        isDeleted: true
                    })
                }
            } catch (err) { }
        }
    }

    let searchRegistrations = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let res = await axios.post('/api/Product/SearchRegistrations', searchRequest);
            setRegistrations(res.data.items);
            setRecordCount(res.data.recordCount);

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }


    return (
        <Provider
            value={{
                isLoading,
                search,
                selectedItems,
                recordCount,
                registrations,

                updateSearch,
                changePage,
                doSearch,
                updateSelectedItems,
                deleteRegistration,
                selectAllRegistrations,
                restoreRegistration
            }}
        >
            {children}
        </Provider>
    )
}

export default RegistrationProvider;